import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, NgZone } from '@angular/core';
import { Posthog } from '../posthog/posthog';

export interface NeetoCalButtonConfig {
  elementId: string;
  contextMessage?: string;
}

type NeetoCalEmbedConfig = Readonly<{
  type: 'elementClick';
  id: string;
  organization: string;
  elementSelector: string;
  queryParams?: Readonly<Record<string, string>>;
}>;

type NeetoCalQueuedArgs = [NeetoCalEmbedConfig];

type NeetoCalApi = {
  embed: (...args: NeetoCalQueuedArgs) => void;
  q?: NeetoCalQueuedArgs[];
};

interface NeetoCalMessagePayload {
  source: 'neetocal';
  type: 'slot_selected' | 'booking_confirmed' | 'neeto-cal-height-change-event';
  payload?: {
    date?: string;
    time?: string;
    time_zone?: string;
    duration?: number;
    preferred_meeting_spot?: string;
  };
}

declare global {
  interface Window {
    neetoCal?: NeetoCalApi;
    __neetoCalListenerBound?: boolean;
  }
}

@Injectable({ providedIn: 'root' })
export class NeetoCal {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly posthog = inject(Posthog);
  private readonly ngZone = inject(NgZone);

  private isScriptLoaded = false;
  private isScriptLoading = false;
  private readonly initializedElements = new WeakSet<Element>();
  private readonly buttonConfigs = new Map<string, NeetoCalButtonConfig>();

  initialize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.bootstrapApi();

    if (!this.isScriptLoaded && !this.isScriptLoading) {
      this.isScriptLoading = true;

      const script = this.document.createElement('script');
      script.async = true;
      script.src = 'https://cdn.neetocal.com/javascript/embed.js';
      script.onload = () => {
        this.isScriptLoaded = true;
        this.isScriptLoading = false;
        this.initializeButtons();
      };
      script.onerror = () => {
        this.isScriptLoaded = false;
        this.isScriptLoading = false;
      };

      this.document.head.appendChild(script);
    } else if (this.isScriptLoaded) {
      this.initializeButtons();
    }
  }

  registerButtons(configs: NeetoCalButtonConfig[]): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    for (const config of configs) {
      this.buttonConfigs.set(config.elementId, config);
    }

    this.initialize();
  }

  private bootstrapApi(): void {
    if (window.neetoCal) {
      return;
    }

    const queuedApi: NeetoCalApi = {
      q: [],
      embed: (...args: NeetoCalQueuedArgs) => {
        queuedApi.q?.push(args);
      },
    };

    window.neetoCal = queuedApi;

    // CRITICAL FIX: Safe tracking initialization only on initial API bootstrap
    this.listenToSchedulingEvents();
  }

  private listenToSchedulingEvents(): void {
    if (window.__neetoCalListenerBound) return;
    window.__neetoCalListenerBound = true;

    window.addEventListener('message', (event: MessageEvent) => {
      if (!event.origin.includes('sparecores.com') && !event.origin.includes('neetocal.com')) {
        return;
      }

      if (!event.data || typeof event.data !== 'object') {
        return;
      }

      const rawEvent = event.data as Partial<NeetoCalMessagePayload>;

      const isConfirmed = rawEvent.source === 'neetocal' && rawEvent.type === 'booking_confirmed';

      if (isConfirmed) {
        const innerPayload = rawEvent.payload ?? {};

        this.ngZone.run(() => {
          this.posthog.capture('calendar event scheduled', {
            meetingName: `${innerPayload.duration ?? 30} mins Sync`,
            hostName: innerPayload.preferred_meeting_spot ?? 'Google Meet',
          });
        });
      }
    });
  }

  private initializeButtons(): void {
    for (const [elementId, config] of this.buttonConfigs.entries()) {
      const element = this.document.getElementById(elementId);

      if (!element || this.initializedElements.has(element)) {
        continue;
      }

      this.initializedElements.add(element);

      window.neetoCal?.embed({
        type: 'elementClick',
        id: '7c3c0ad8-812b-423f-b064-0ea44d527368',
        organization: 'sparecores',
        elementSelector: `#${elementId}`,
        queryParams: config.contextMessage
          ? {
              context: config.contextMessage,
            }
          : undefined,
      });
    }
  }
}

export const neetoCalService = () => inject(NeetoCal);
